import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { ShieldCheck, MapPin, Phone, Clock, Search, Navigation, Send } from "lucide-react";
import { api, fileUrl, waLink } from "@/lib/api";
import { PartCard, Loader, Stars, Empty } from "@/components/shared";
import { mapEmbedUrl, directionsUrl } from "@/components/LocationBar";

export default function YardDetail() {
  const { id } = useParams();
  const [yard, setYard] = useState(null);
  const [parts, setParts] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [q, setQ] = useState("");

  useEffect(() => {
    window.scrollTo(0, 0);
    api.get(`/yards/${id}`).then(({ data }) => setYard(data));
    api.get(`/yards/${id}/parts`).then(({ data }) => setParts(data));
    api.get(`/yards/${id}/reviews`).then(({ data }) => setReviews(data.filter(r => !r.hidden)));
  }, [id]);

  if (!yard) return <Loader />;
  const filtered = parts.filter((p) => p.name.includes(q));

  const shareYardWhatsApp = () => {
    const url = window.location.href;
    const text = `🏪 ${yard.name}${yard.verified ? " (تشليح موثق)" : ""}\n⭐ التقييم: ${yard.rating_avg} (${yard.rating_count})\n📍 ${yard.city} - ${yard.address}\n🔧 ${yard.parts_count} قطعة متوفرة\n🔗 ${url}\n\nعبر منصة مخزون كار`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");
  };

  return (
    <div>
      <div className="relative h-48 sm:h-64 overflow-hidden">
        <img src={yard.logo ? fileUrl(yard.logo) : "https://images.unsplash.com/photo-1625047509248-ec889cbff17f?w=1600"} alt="" className="h-full w-full object-cover opacity-40" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(11,15,23,0.4), var(--bg-primary))" }} />
      </div>
      <div className="mx-auto max-w-7xl px-4 sm:px-6 -mt-20 relative">
        <div className="ts-card p-6 flex flex-col sm:flex-row gap-5 items-start">
          <div className="h-24 w-24 shrink-0 overflow-hidden rounded-2xl bg-[var(--bg-secondary)] border-2 border-[var(--amber)]">
            {yard.logo ? <img src={fileUrl(yard.logo)} alt="" className="h-full w-full object-cover" /> :
              <div className="flex h-full items-center justify-center font-head text-4xl font-black text-[var(--amber)]">{yard.name?.[0]}</div>}
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="font-head text-2xl font-black text-white">{yard.name}</h1>
              {yard.verified && <span className="inline-flex items-center gap-1 rounded-lg bg-[var(--emerald)]/15 px-2 py-1 text-xs font-bold text-[var(--emerald)]"><ShieldCheck size={14} /> تشليح موثق</span>}
            </div>
            <div className="flex items-center gap-2 mt-2 text-sm"><Stars value={yard.rating_avg} /><span className="text-[var(--text-muted)]">{yard.rating_avg} ({yard.rating_count} تقييم)</span></div>
            <div className="grid sm:grid-cols-3 gap-3 mt-4 text-sm text-[var(--text-muted)]">
              <span className="flex items-center gap-1.5"><MapPin size={15} className="text-[var(--amber)]" />{yard.address}</span>
              <span className="flex items-center gap-1.5"><Clock size={15} className="text-[var(--amber)]" />{yard.working_hours}</span>
              <span className="flex items-center gap-1.5"><Phone size={15} className="text-[var(--amber)]" />{yard.contact}</span>
            </div>
          </div>
          <div className="text-center px-4">
            <p className="font-head text-3xl font-black text-[var(--amber)]">{yard.parts_count}</p>
            <p className="text-xs text-[var(--text-muted)]">قطعة متوفرة</p>
          </div>
        </div>
        <div className="mt-4 flex flex-wrap items-center gap-3">
          {(yard.contact || yard.mobile) && (
            <a data-testid="whatsapp-yard-button" href={waLink(yard.contact || yard.mobile, `مرحبًا ${yard.name}، تواصلت معكم عبر منصة مخزون كار.`)}
              target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#25D366] px-5 py-3 font-bold text-white hover:bg-[#1ebe57] transition-colors">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.5 2 2 6.5 2 12c0 1.8.5 3.5 1.3 5L2 22l5.2-1.3c1.4.8 3 1.2 4.7 1.2 5.5 0 10-4.5 10-10S17.5 2 12 2zm0 18c-1.5 0-3-.4-4.2-1.2l-.3-.2-3.1.8.8-3-.2-.3C4.4 15 4 13.5 4 12c0-4.4 3.6-8 8-8s8 3.6 8 8-3.6 8-8 8zm5.5-5.6c-.3-.1-1.7-.9-2-1-.3-.1-.5-.1-.6.2-.2.3-.7.9-.8 1-.2.2-.3.2-.6.1-.3-.2-1.2-.5-2.3-1.4-.9-.8-1.4-1.7-1.6-2-.2-.3 0-.5.1-.6.1-.1.3-.4.4-.5.1-.2.2-.3.3-.5.1-.2 0-.4 0-.5s-.6-1.5-.9-2c-.2-.5-.4-.4-.6-.4h-.5c-.2 0-.5.1-.7.3-.3.3-1 1-1 2.4s1 2.8 1.2 3c.1.2 2 3.1 4.9 4.3.7.3 1.2.5 1.6.6.7.2 1.3.2 1.8.1.5-.1 1.7-.7 2-1.4.2-.7.2-1.2.2-1.4-.1-.1-.3-.2-.6-.3z"/></svg>
              تواصل عبر واتساب
            </a>
          )}
          <button data-testid="share-yard-whatsapp-button" onClick={shareYardWhatsApp}
            className="inline-flex items-center justify-center gap-2 rounded-xl border border-[#25D366] px-5 py-3 font-bold text-[#25D366] hover:bg-[#25D366]/10 transition-colors">
            <Send size={18} /> شارك التشليح عبر واتساب
          </button>
          {yard.lat != null && yard.lng != null && (
            <a data-testid="yard-header-directions-button" href={directionsUrl(yard.lat, yard.lng)} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 rounded-xl border border-[var(--border-c)] px-5 py-3 font-bold text-white hover:border-[var(--amber)] transition-colors">
              <Navigation size={18} /> الاتجاهات
            </a>
          )}
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 sm:px-6 py-8">
        {yard.lat != null && yard.lng != null && (
          <div className="ts-card overflow-hidden mb-6" data-testid="yard-map-section">
            <div className="flex items-center justify-between p-4 border-b" style={{ borderColor: "var(--border-c)" }}>
              <h2 className="font-head text-lg font-bold text-white flex items-center gap-2"><MapPin size={18} className="text-[var(--amber)]" /> موقع التشليح على الخريطة</h2>
              <a data-testid="yard-directions-button" href={directionsUrl(yard.lat, yard.lng)} target="_blank" rel="noopener noreferrer"
                className="btn-amber !py-2 !px-4 text-sm"><Navigation size={16} /> الاتجاهات</a>
            </div>
            <div className="relative">
              <iframe title="موقع التشليح" data-testid="yard-map-iframe" src={mapEmbedUrl(yard.lat, yard.lng)}
                className="w-full h-64 sm:h-80" style={{ border: 0 }} loading="lazy" />
              <a data-testid="yard-map-directions-button" href={directionsUrl(yard.lat, yard.lng)} target="_blank" rel="noopener noreferrer"
                className="absolute bottom-4 left-4 btn-amber !py-2.5 !px-4 text-sm shadow-lg amber-glow">
                <Navigation size={16} /> الاتجاهات من هنا
              </a>
            </div>
          </div>
        )}
        <div className="flex items-center gap-2 mb-6 rounded-xl bg-[var(--bg-card)] border px-4" style={{ borderColor: "var(--border-c)" }}>
          <Search size={18} className="text-[var(--text-muted)]" />
          <input data-testid="yard-inventory-search" placeholder="ابحث داخل قطع هذا التشليح" onChange={(e) => setQ(e.target.value)}
            className="w-full bg-transparent py-3 text-white outline-none" />
        </div>
        {filtered.length === 0 ? <Empty text="لا توجد قطع مطابقة في هذا التشليح." /> : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6">
            {filtered.map((p) => <PartCard key={p.id} part={p} />)}
          </div>
        )}

        {reviews.length > 0 && (
          <section className="mt-14">
            <h2 className="font-head text-xl font-black text-white mb-5">تقييمات العملاء</h2>
            <div className="grid gap-4 sm:grid-cols-2">
              {reviews.map((r) => (
                <div key={r.id} className="ts-card p-4">
                  <div className="flex items-center justify-between"><span className="font-bold text-white">{r.customer_name}</span><Stars value={r.stars} /></div>
                  {r.comment && <p className="text-sm text-[var(--text-muted)] mt-2">{r.comment}</p>}
                </div>
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
